import * as React from 'react';
import ChartLib from 'chart.js';

import { Chart } from '../../components/Chart';
import { usePrepareChartConfig } from './utils/usePrepareChartConfig';
import { TChartData, TDealType, EChartType } from '../../types/charts';
import { getPriceUnit } from './utils/getPriceUnit';
import { TDevice } from '@cian/utils';
import { getChartHeight } from './utils/getChartHeight';
import { ChartLayout } from '../../components/ChartLayout';
import { Preloader } from '../../components/Preloader';
import { useOnScroll } from './utils/useOnScroll';

interface IChartContainerProps {
  chartData: TChartData;
  dealType: TDealType;
  firstLabel: string;
  chartType: EChartType;
  deviceType: TDevice;
  loading: boolean;
  secondLabel?: string;
  onHover?(): void;
  onSwipe?(): void;
}

export const ChartFactory: React.FC<IChartContainerProps> = props => {
  const { dealType, chartData, firstLabel, secondLabel, chartType, onHover, onSwipe, deviceType, loading } = props;

  const chartCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [chartCtx, setChartCtx] = React.useState<CanvasRenderingContext2D | null>(null);
  const [chart, setChart] = React.useState<ChartLib | null>(null);

  const priceUnit = getPriceUnit(dealType, chartType);

  const chartHeight = getChartHeight(deviceType);

  const chartConfig = usePrepareChartConfig(chartCtx, {
    ...chartData,
    firstLabel,
    secondLabel,
    priceUnit,
    chartType,
    chartHeight,
    onHover,
  });

  // Глобальные настройки для chart.js
  React.useEffect(() => {
    ChartLib.defaults.fontFamily = 'Lato, Arial, sans-serif';
    ChartLib.defaults.fontSize = 12;
  }, []);

  React.useEffect(() => {
    // Сохраняем canvas context в стейт
    /* istanbul ignore else */
    if (chartCanvasRef.current) {
      const ctx = chartCanvasRef.current.getContext('2d');
      setChartCtx(ctx);
    }

    // Скроллит блок с графиком в конец графика
    /* istanbul ignore else */
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, []);

  const onScroll = useOnScroll(scrollContainerRef, onSwipe);

  React.useEffect(() => {
    if (chartCtx && chartConfig && !chart) {
      const chart = new ChartLib(chartCtx, chartConfig);
      setChart(chart);
    }
  }, [chartCtx, chartConfig, chart, setChart]);

  React.useEffect(() => {
    if (chart) {
      for (let i = 0; i < chartConfig.data.datasets.length; i++) {
        chart.data.datasets[i].data = chartConfig.data.datasets[i].data;
        chart.data.datasets[i].label = chartConfig.data.datasets[i].label;
      }

      chart.data.labels = chartConfig.data.labels;
      chart.options = Object.assign({}, chartConfig.options);

      chart.update();
    }
  }, [chartConfig, chart]);

  return (
    <ChartLayout>
      {loading && <Preloader />}
      <Chart
        chartCanvasRef={chartCanvasRef}
        scrollContainerRef={scrollContainerRef}
        chartHeight={chartHeight}
        onScroll={onScroll}
      />
    </ChartLayout>
  );
};

ChartFactory.displayName = 'ChartContainer';
